import Modal from '../../Unknown/Modal'
import { useAppDispatch, useAppSelector } from '../../../modules/store/hooks'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import { Box, Button, TextField } from '@mui/material'
import translate from '../../../i18n/translate'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { Formik, Form } from 'formik'
import { FIELDS, useValidationSchema } from './useValidationSchema'
import { organizerApi } from '../../../modules/services/organizerService'

type OrganizerSettingsEventFeePercentModalProps = {
  organizerSettingsInfo: {
    id: number
    vatPercent: number
    feePercent: number
    defaultFeePercent: number
  }
  handleUpdateSuccess: (
    id: number, 
    data: {
      feePercent: number;
    },
  ) => void;
}

export const WITHDRAWAL_TYPES = {
  DEFAULT: 'default',
  DAYS: 'days'
}

export default function OrganizerSettingsEventFeePercentModal({
  organizerSettingsInfo,
  handleUpdateSuccess
}: OrganizerSettingsEventFeePercentModalProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { toggleOrganizerSettingsEventFeePercentModal } = modalsSlice.actions
  const visible = useAppSelector(state => state.modalsReducer.organizerSettingsEventFeePercent)

  const [
    updateOrganizerSettingsEventFee,
    { isLoading }
  ] = organizerApi.useUpdateOrganizerSettingsEventFeeMutation()

  const handleSubmit = async (values: any) => {
    try {
      if (organizerSettingsInfo?.id) {
        const data = {
          feePercent: values?.[FIELDS.FEE_PERCENT] ? Number((values?.[FIELDS.FEE_PERCENT] / 100).toFixed(4)) : organizerSettingsInfo.defaultFeePercent,
        }

        await updateOrganizerSettingsEventFee({
          userId: organizerSettingsInfo.id,
          ...data,
        });

        handleUpdateSuccess(organizerSettingsInfo.id, data);
      }
      dispatch(toggleOrganizerSettingsEventFeePercentModal())
    } catch (error) {
      toast.error(translate('updateFeePercentError'));
    }
  }

  const onCloseModal = () => {
    dispatch(toggleOrganizerSettingsEventFeePercentModal())
  }

  const validationSchema = useValidationSchema()

  return (
    <Modal
      title={translate('organizerSettingsEventFeePercentModalTitle')}
      visible={visible === 'open'}
      style={{ maxWidth: '500px' }}
      setVisible={onCloseModal}
    >
      <Formik
        initialValues={{
          [FIELDS.FEE_PERCENT]: Number((organizerSettingsInfo?.feePercent !== null ? organizerSettingsInfo?.feePercent : organizerSettingsInfo.defaultFeePercent) * 100).toFixed(2),
        }}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={validationSchema}
      >
        {({ errors, values, touched, handleBlur, handleChange, setFieldValue, isValid }) => {
          return (
            <Form>
              <Box
                my={2}
                p={2}
                display='flex'
                flexDirection='column'
                justifyContent='center'
                width={1}
                alignItems='center'
                gap={2}
              >
                <TextField
                  name={FIELDS.FEE_PERCENT}
                  value={values?.[FIELDS.FEE_PERCENT]}
                  error={touched?.[FIELDS.FEE_PERCENT] && Boolean(errors?.[FIELDS.FEE_PERCENT])}
                  helperText={touched?.[FIELDS.FEE_PERCENT] && errors?.[FIELDS.FEE_PERCENT]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant='outlined'
                  disabled={isLoading}
                  fullWidth
                  type='number'
                  label={intl.formatMessage({ id: 'organizerEventFee' })}
                  placeholder={intl.formatMessage({ id: 'organizerEventFee' })}
                  dir='ltr'
                  sx={{
                    '.MuiInputBase-root.MuiInput-root:before': {
                      borderBottom: '0px'
                    },
                    '.MuiInputBase-root.MuiInput-root:hover': {
                      borderBottom: '0px'
                    }
                  }}
                />
                <Button
                  size='large'
                  variant='contained'
                  color='warning'
                  type='submit'
                  fullWidth
                  disabled={isLoading || !isValid}
                >
                  {translate('organizerUpdateEventFeeSettings')}
                </Button>
              </Box>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}
